import { PusherChannel, PusherEvent } from '@mondaygpt/constants'
import type { PusherFeatureUsageUpdateData } from 'modules/billing/types'
import { selectCurrentUser, selectIsLoggedIn } from 'modules/common'
import { PusherService } from 'openapi'
import Pusher, { AuthorizerCallback, Channel } from 'pusher-js'
import { ChannelAuthorizationData } from 'pusher-js/types/src/core/auth/options'
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { errorReportingService } from 'services/error-reporting/error-reporting.service'
import { useAppSelector } from '../hooks'
import { apiCall } from '../services/api'

interface PusherEventHandlers {
  onOAuthUpdated?: (data: { provider: string }) => void
  onFeatureUsageUpdated?: (data: PusherFeatureUsageUpdateData) => void
  onSubscriptionUpdated?: () => void
}

interface PusherContext {
  isConnected: boolean
  registerHandlers: (handlers: PusherEventHandlers) => void
  unregisterHandler: (handler: keyof PusherEventHandlers) => void
  userChannel: Channel | null
  subscribeToChannel: (channelName: string) => Channel | null
  unsubscribeFromChannel: (channelName: string) => void
}

const PusherContext = createContext<PusherContext>({
  isConnected: false,
  registerHandlers: () => {},
  unregisterHandler: () => {},
  userChannel: null,
  subscribeToChannel: () => null,
  unsubscribeFromChannel: () => {},
})

export const usePusher = () => useContext(PusherContext)

interface Props {
  children: React.ReactNode
}

export const PusherProvider = ({ children }: Props) => {
  const [pusher, setPusher] = useState<Pusher | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const isLoggedIn = useAppSelector(selectIsLoggedIn)
  const currentUser = useAppSelector(selectCurrentUser)
  const eventHandlersRef = useRef<PusherEventHandlers>({})
  const subscribedChannels = useRef<Record<string, Channel>>({})
  const [userChannel, setUserChannel] = useState<Channel | null>(null)

  // Use useCallback to create stable reference to registerHandlers
  const registerHandlers = useCallback((handlers: PusherEventHandlers) => {
    eventHandlersRef.current = { ...eventHandlersRef.current, ...handlers }
  }, [])

  const unregisterHandler = useCallback((handler: keyof PusherEventHandlers) => {
    const { [handler]: _h, ...rest } = eventHandlersRef.current
    eventHandlersRef.current = rest
  }, [])

  const subscribeToChannel = useCallback(
    (channelName: string): Channel | null => {
      if (!pusher) return null
      if (subscribedChannels.current[channelName]) {
        return subscribedChannels.current[channelName]
      }
      const channel = pusher.subscribe(channelName)
      subscribedChannels.current[channelName] = channel
      return channel
    },
    [pusher]
  )

  const unsubscribeFromChannel = useCallback(
    (channelName: string) => {
      if (!pusher) return
      pusher.unsubscribe(channelName)
      delete subscribedChannels.current[channelName]
    },
    [pusher]
  )

  useEffect(() => {
    const handleOnline = () => setIsConnected(true)
    const handleOffline = () => setIsConnected(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  useEffect(() => {
    // Only connect to Pusher when the user is logged in
    if (!isLoggedIn || !currentUser) {
      if (pusher) {
        pusher.disconnect()
        setPusher(null)
        setIsConnected(false)
      }
      return
    }

    try {
      // Custom authorizer function that uses apiCall for token refresh capability
      const customAuthorizer = (channel: { name: string }) => {
        return {
          authorize: async (socketId: string, callback: AuthorizerCallback) => {
            try {
              // Use PusherService with apiCall for token refresh capability
              const authResponse = await apiCall(async () => {
                return await PusherService.pusherControllerAuthenticateV1({
                  socket_id: socketId,
                  channel_name: channel.name,
                })
              }, null)

              // The server response should already have the format expected by Pusher
              callback(null, authResponse as ChannelAuthorizationData)
            } catch (error) {
              errorReportingService.captureException(
                error instanceof Error ? error : new Error('Pusher authentication error'),
                {
                  feature: 'realtime',
                  step: 'auth',
                  channel: channel.name,
                  userId: currentUser?.id,
                }
              )
              console.error('Pusher authentication error:', error)
              callback(error as Error, null)
            }
          },
        }
      }

      const pusherInstance = new Pusher(import.meta.env.VITE_PUSHER_KEY, {
        cluster: import.meta.env.VITE_PUSHER_CLUSTER,
        authorizer: customAuthorizer,
      })

      // Setup connection status handler
      pusherInstance.connection.bind(PusherEvent.Connected, () => {
        Pusher.log?.('Pusher connected successfully!')
        setIsConnected(true)

        // Subscribe to user's private channel for notifications
        const userChannel = pusherInstance.subscribe(
          `${PusherChannel.Private}-${PusherChannel.User}-${currentUser.id}`
        )

        setUserChannel(userChannel)

        userChannel.bind(PusherEvent.OAuthUpdated, (data: { provider: string }) => {
          eventHandlersRef.current.onOAuthUpdated?.(data)
          // Dispatch custom DOM event for oauth-utils to listen to (event-driven, no polling)
          window.dispatchEvent(new CustomEvent('oauth-server-success', { detail: data }))
        })

        userChannel.bind(PusherEvent.FeatureUsageUpdated, (data: PusherFeatureUsageUpdateData) => {
          eventHandlersRef.current.onFeatureUsageUpdated?.(data)
        })

        userChannel.bind(PusherEvent.SubscriptionUpdated, () => {
          eventHandlersRef.current.onSubscriptionUpdated?.()
        })

        subscribedChannels.current[
          `${PusherChannel.Private}-${PusherChannel.User}-${currentUser.id}`
        ] = userChannel
      })

      pusherInstance.connection.bind(PusherEvent.Disconnected, () => {
        Pusher.log?.('Pusher disconnected')
        setIsConnected(false)
        setUserChannel(null)
      })

      pusherInstance.connection.bind(PusherEvent.Unavailable, () => {
        Pusher.log?.('Pusher unavailable')
        setIsConnected(false)
        setUserChannel(null)
      })

      pusherInstance.connection.bind(PusherEvent.Error, (error: Error | unknown) => {
        setIsConnected(false)
        setUserChannel(null)

        // Transient connection errors are handled by the Pusher SDK's built-in retry
        const isBrowserEvent = typeof error === 'object' && error !== null && 'isTrusted' in error
        const errorData = error instanceof Error ? error.message : JSON.stringify(error)
        const isTransient =
          isBrowserEvent ||
          /1006|4201|pong timeout|WebSocket is closed before|abnormal closure/i.test(errorData)
        if (isTransient) {
          // SDK handles retry internally — don't report to error tracking
          Pusher.log?.(`Pusher transient connection error (will auto-retry): ${errorData}`)
        } else {
          errorReportingService.captureMessage(`Pusher connection error: ${errorData}`, 'warning', {
            feature: 'realtime',
            step: 'connection',
            userId: currentUser?.id,
          })
          console.warn('Pusher connection error:', error)
        }
      })

      setPusher(pusherInstance)

      // Cleanup on unmount
      return () => {
        Object.keys(subscribedChannels.current).forEach(channelName => {
          pusherInstance.unsubscribe(channelName)
        })
        subscribedChannels.current = {}
        pusherInstance.disconnect()
        setPusher(null)
        setIsConnected(false)
        setUserChannel(null)
      }
    } catch (error) {
      errorReportingService.captureException(
        error instanceof Error ? error : new Error('Failed to initialize Pusher'),
        { feature: 'realtime', step: 'init', userId: currentUser?.id }
      )
      console.error('Failed to initialize Pusher:', error)
    }
  }, [isLoggedIn, currentUser?.id])

  return (
    <PusherContext.Provider
      value={{
        isConnected,
        registerHandlers,
        unregisterHandler,
        userChannel,
        subscribeToChannel,
        unsubscribeFromChannel,
      }}
    >
      {children}
    </PusherContext.Provider>
  )
}
